import React from "react";
import { isMobile } from "@scrile/tools/dist/lib/browserUtils";
import { useTranslation } from "locales";
import useBreakPoints from "hooks/useBreakPoints";
import useMobileKeyboardOpen from "hooks/useMobileKeyboard";
import { MediaConstraints, MediaDevicePermissions } from "types";
import BaseHint from "components/BaseHint";
import BaseIcon from "components/BaseIcon";
import VideoControlButton from "components/VideoControlButton";
import config from "../../../../config";
import TransitionControl from "../TransitionControl";
import VolumeControl from "../VolumeControl";
import "./style.scss";

interface Props {
  loading: boolean;
  showControlsButtons: boolean;
  constraints: MediaConstraints | null;
  onClickEndCall?: () => void;
  onSwitchCam: () => void;
  onToggleMic: () => void;
  onToggleCam: () => void;
  onToggleBlur: () => void;
  onToggleScreenSharing?: () => void;
  onToggleGrid?: () => void;
  player: HTMLVideoElement | null;
  slotControlButtons?: JSX.Element;
  className: string;
  showChatButton?: boolean;
  onToggleChat?: () => void;
  muted?: boolean;
  showGridBtn?: boolean;
  hasUnreadMessage?: boolean;
  permissions?: MediaDevicePermissions;
  hintPosition?: "top" | "right" | "bottom" | "left";
}

function PanelControlButtons({
  loading,
  showControlsButtons,
  constraints,
  onClickEndCall,
  onSwitchCam,
  onToggleMic,
  onToggleCam,
  onToggleBlur,
  onToggleScreenSharing,
  player,
  className,
  slotControlButtons,
  showChatButton,
  onToggleChat,
  muted,
  showGridBtn,
  onToggleGrid,
  hasUnreadMessage,
  permissions,
  hintPosition = "top",
}: Props) {
  const { desktopUp } = useBreakPoints();
  const { keyboardOpen } = useMobileKeyboardOpen();
  const { t } = useTranslation();

  const cls = ["panel-control-buttons"];
  cls.push(className);

  const hintActivator = <div className="panel-control-buttons__hint"></div>;
  const marker = <div className="panel-control-buttons__marker"></div>;
  const warningMarker = <div className="panel-control-buttons__marker __warning"></div>;

  const cameraPermission = permissions?.camera;
  const microphonePermission = permissions?.microphone;
  const cameraDenied = cameraPermission === "denied";
  const microphoneDenied = microphonePermission === "denied";
  const cameraNotGranted = cameraPermission !== undefined && cameraPermission !== "granted";
  const microphoneNotGranted = microphonePermission !== undefined && microphonePermission !== "granted";

  const toggleGridBtn = (
    <VideoControlButton
      className="panel-control-buttons__btn __grid"
      name="toggle-grid"
      toggle
      borderless
      onClick={onToggleGrid}
      disabled={loading}
    >
      <BaseIcon>grid</BaseIcon>
    </VideoControlButton>
  );

  return (
    <>
      <TransitionControl showControl={showControlsButtons && !keyboardOpen} className={cls.join(" ")}>
        {showGridBtn && <div className="panel-control-buttons__void"></div>}
        {onClickEndCall && (
          <VideoControlButton className="panel-control-buttons__btn" name="end-chat" negative onClick={onClickEndCall}>
            <BaseIcon>call</BaseIcon>
          </VideoControlButton>
        )}
        {constraints && (
          <>
            {!desktopUp && constraints.videoEnabled && (
              <VideoControlButton
                className="panel-control-buttons__btn"
                name="switch-cam"
                borderless
                onClick={onSwitchCam}
                disabled={loading}
              >
                <BaseIcon>switch-camera</BaseIcon>
              </VideoControlButton>
            )}
            <BaseHint
              enableHint={cameraDenied}
              text={t("Camera permission denied")}
              activator={hintActivator}
              position={hintPosition}
            >
              <VideoControlButton
                className="panel-control-buttons__btn"
                name="toggle-cam"
                toggle={constraints.videoEnabled}
                borderless
                onClick={onToggleCam}
                disabled={loading || cameraDenied}
              >
                <BaseIcon>{constraints.videoEnabled ? "cam_on" : "cam_off"}</BaseIcon>
                {cameraNotGranted && warningMarker}
              </VideoControlButton>
            </BaseHint>
            <BaseHint
              enableHint={muted || microphoneDenied}
              text={muted ? t("You are muted") : t("Microphone permission denied")}
              activator={hintActivator}
              position={hintPosition}
            >
              <VideoControlButton
                className="panel-control-buttons__btn"
                name="toggle-mic"
                toggle={constraints.audioEnabled}
                borderless
                onClick={onToggleMic}
                disabled={loading || muted || microphoneDenied}
              >
                <BaseIcon>{constraints.audioEnabled ? "mic_on" : "mic_off"}</BaseIcon>
                {microphoneNotGranted && warningMarker}
              </VideoControlButton>
            </BaseHint>
            {config.featureScreenSharing && !isMobile() && onToggleScreenSharing && (
              <VideoControlButton
                className="panel-control-buttons__btn"
                name="toggle-screen"
                toggle={constraints.shareEnabled}
                borderless
                onClick={onToggleScreenSharing}
                disabled={loading}
              >
                <BaseIcon>{constraints.shareEnabled ? "screen-share_off" : "screen-share_on"}</BaseIcon>
              </VideoControlButton>
            )}
          </>
        )}
        {slotControlButtons}
        {constraints?.videoEnabled && (
          <VideoControlButton
            className="panel-control-buttons__btn"
            name="toggle-blur"
            toggle={constraints.blurEnabled}
            borderless
            onClick={onToggleBlur}
            disabled={loading}
          >
            <BaseIcon>{constraints.blurEnabled ? "sparkle" : "sparkle_off"}</BaseIcon>
          </VideoControlButton>
        )}
        {player && <VolumeControl player={player} className="panel-control-buttons__btn" />}
        {showChatButton && onToggleChat && (
          <VideoControlButton
            className="panel-control-buttons__btn"
            name="toogle-chat"
            borderless
            onClick={onToggleChat}
          >
            <BaseIcon>send</BaseIcon>
            {hasUnreadMessage && marker}
          </VideoControlButton>
        )}
        {desktopUp && showGridBtn && toggleGridBtn}
      </TransitionControl>
      {!desktopUp && showGridBtn && (
        <TransitionControl
          showControl={showControlsButtons && !keyboardOpen}
          className={[...cls, "additional-controls"].join(" ")}
        >
          {toggleGridBtn}
        </TransitionControl>
      )}
    </>
  );
}

export default PanelControlButtons;
